import React, { Component } from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Card,
  Descriptions,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Alert,
  Tag,
  Typography,
  Upload,
  notification
} from 'antd'
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
  PhoneOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import DashboardLayout from '../../layout/dashboardLayout/DashboardLayout'
import { controller } from '../../controller'
import { carrierCallApi } from './carrierCallApi'
import { apiService } from '../../_services/api.service'
import '../addProvider/style.css'
import '../officeAdminPage.css'

var { Title, Text, Paragraph } = Typography
var { TabPane } = Tabs
var { TextArea } = Input

function statusColor (s) {
  switch (s) {
    case 'draft': return 'default'
    case 'pending_review': return 'warning'
    case 'approved': return 'success'
    case 'disabled': return 'error'
    default: return 'default'
  }
}

function recordingStatusColor (s) {
  switch (s) {
    case 'uploaded': return 'default'
    case 'transcribing': return 'processing'
    case 'analyzing': return 'processing'
    case 'complete': return 'success'
    case 'failed': return 'error'
    default: return 'default'
  }
}

class InsuranceCarrierCallAdmin extends Component {
  state = {
    serverLogo: '',
    loading: true,
    configs: [],
    // Detail view
    selectedId: null,
    detail: null,
    detailLoading: false,
    // Create modal
    createModalOpen: false,
    providers: [],
    creating: false,
    // Test call modal
    testCallModalOpen: false,
    testCallPolicyId: '',
    testCalling: false,
    // Editing
    saving: false,
    editedFields: {},
    // Intake (auto-detect carrier from recording) modal
    intakeModalOpen: false,
    intakeFile: null,
    intakeUploading: false,
    intakeRecording: null,
    intakeBinding: false,
    intakeOverrideProviderId: null
  }

  intakePollTimer = null

  async componentDidMount () {
    var logo = await controller.getLogo()
    this.setState({ serverLogo: logo.data && logo.data.dark ? logo.data.dark : '' })
    this.loadConfigs()
    this.loadProviders()
  }

  loadConfigs = async () => {
    this.setState({ loading: true })
    try {
      var res = await carrierCallApi.list()
      this.setState({ configs: res || [], loading: false })
    } catch (e) {
      notification.error({ message: 'Error', description: 'Failed to load carrier configs' })
      this.setState({ loading: false })
    }
  }

  loadProviders = async () => {
    try {
      var res = await apiService.get('/clinics/insurance-providers/')
      this.setState({ providers: res || [] })
    } catch (e) {
      // Providers list is non-critical
    }
  }

  openDetail = async (id) => {
    this.setState({ selectedId: id, detailLoading: true, editedFields: {} })
    try {
      var res = await carrierCallApi.detail(id)
      this.setState({ detail: res, detailLoading: false })
    } catch (e) {
      notification.error({ message: 'Error', description: 'Failed to load config detail' })
      this.setState({ detailLoading: false })
    }
  }

  backToList = () => {
    this.setState({ selectedId: null, detail: null, editedFields: {} })
    this.loadConfigs()
  }

  // ── Create ──

  openCreateModal = () => this.setState({ createModalOpen: true })
  closeCreateModal = () => this.setState({ createModalOpen: false })

  handleCreate = async (values) => {
    this.setState({ creating: true })
    try {
      await carrierCallApi.create(values)
      notification.success({ message: 'Created', description: 'Carrier config created.' })
      this.closeCreateModal()
      this.loadConfigs()
    } catch (e) {
      notification.error({ message: 'Error', description: 'Failed to create config.' })
    }
    this.setState({ creating: false })
  }

  // ── Intake (auto-detect carrier) ──

  openIntakeModal = () => this.setState({
    intakeModalOpen: true,
    intakeFile: null,
    intakeRecording: null,
    intakeOverrideProviderId: null
  })

  closeIntakeModal = () => {
    this.stopIntakePolling()
    this.setState({
      intakeModalOpen: false,
      intakeFile: null,
      intakeUploading: false,
      intakeRecording: null,
      intakeBinding: false,
      intakeOverrideProviderId: null
    })
  }

  stopIntakePolling = () => {
    if (this.intakePollTimer) {
      clearInterval(this.intakePollTimer)
      this.intakePollTimer = null
    }
  }

  handleIntakeUpload = async () => {
    var { intakeFile } = this.state
    if (!intakeFile) return
    this.setState({ intakeUploading: true })
    try {
      var fd = new FormData()
      fd.append('audio_file', intakeFile)
      var rec = await carrierCallApi.intakeUpload(fd)
      this.setState({ intakeRecording: rec, intakeUploading: false })
      this.startIntakePolling(rec.recording_id)
    } catch (e) {
      notification.error({ message: 'Upload failed', description: 'Could not upload recording.' })
      this.setState({ intakeUploading: false })
    }
  }

  startIntakePolling = (recordingId) => {
    this.stopIntakePolling()
    this.intakePollTimer = setInterval(async () => {
      try {
        var rec = await carrierCallApi.intakeStatus(recordingId)
        this.setState({ intakeRecording: rec })
        if (rec.status === 'identified' || rec.status === 'failed') {
          this.stopIntakePolling()
        }
      } catch (e) {
        // transient — keep polling
      }
    }, 3000)
  }

  handleIntakeConfirm = async () => {
    var { intakeRecording, intakeOverrideProviderId } = this.state
    if (!intakeRecording) return
    var providerId = intakeOverrideProviderId
      || (intakeRecording.suggested_provider && intakeRecording.suggested_provider.provider_id)
    if (!providerId) {
      notification.warning({ message: 'Pick a carrier', description: 'Select an insurance carrier to continue.' })
      return
    }
    this.setState({ intakeBinding: true })
    try {
      var res = await carrierCallApi.bindRecordingToProvider(intakeRecording.recording_id, providerId)
      notification.success({
        message: res.config_created ? 'Carrier created' : 'Recording added',
        description: res.config_created
          ? 'New carrier config created from recording.'
          : 'Recording attached to existing carrier config.'
      })
      this.closeIntakeModal()
      this.loadConfigs()
      this.openDetail(res.config_id)
    } catch (e) {
      notification.error({ message: 'Error', description: 'Failed to bind recording to carrier.' })
      this.setState({ intakeBinding: false })
    }
  }

  componentWillUnmount () {
    this.stopIntakePolling()
  }

  // ── Approve / Disable ──

  handleApprove = async () => {
    try {
      await carrierCallApi.approve(this.state.selectedId)
      notification.success({ message: 'Approved', description: 'Carrier is now active for calls.' })
      this.openDetail(this.state.selectedId)
      this.loadConfigs()
    } catch (e) {
      notification.error({ message: 'Error', description: 'Failed to approve.' })
    }
  }

  handleDisable = async () => {
    try {
      await carrierCallApi.disable(this.state.selectedId)
      notification.success({ message: 'Disabled', description: 'Carrier calls disabled.' })
      this.openDetail(this.state.selectedId)
      this.loadConfigs()
    } catch (e) {
      notification.error({ message: 'Error', description: 'Failed to disable.' })
    }
  }

  // ── Save edits ──

  handleFieldChange = (field, value) => {
    this.setState(prev => ({
      editedFields: { ...prev.editedFields, [field]: value }
    }))
  }

  handleSave = async () => {
    var { editedFields, selectedId } = this.state
    if (Object.keys(editedFields).length === 0) return
    this.setState({ saving: true })
    try {
      await carrierCallApi.update(selectedId, editedFields)
      notification.success({ message: 'Saved', description: 'Config updated.' })
      this.openDetail(selectedId)
    } catch (e) {
      notification.error({ message: 'Error', description: 'Failed to save.' })
    }
    this.setState({ saving: false, editedFields: {} })
  }

  // ── Upload ──

  handleUpload = async (file) => {
    var formData = new FormData()
    formData.append('audio_file', file)
    try {
      await carrierCallApi.uploadRecording(this.state.selectedId, formData)
      notification.success({ message: 'Uploaded', description: 'Recording uploaded. Analysis will begin shortly.' })
      this.openDetail(this.state.selectedId)
    } catch (e) {
      notification.error({ message: 'Error', description: 'Upload failed.' })
    }
    return false
  }

  // ── Test Call ──

  openTestCallModal = () => this.setState({ testCallModalOpen: true, testCallPolicyId: '' })
  closeTestCallModal = () => this.setState({ testCallModalOpen: false })

  handleTestCall = async () => {
    this.setState({ testCalling: true })
    try {
      await carrierCallApi.testCall(this.state.selectedId, this.state.testCallPolicyId)
      notification.success({ message: 'Call Initiated', description: 'Test call started.' })
      this.closeTestCallModal()
    } catch (e) {
      notification.error({ message: 'Error', description: 'Failed to initiate test call.' })
    }
    this.setState({ testCalling: false })
  }

  // ── Render: List View ──

  renderList () {
    var { configs, loading, providers, createModalOpen, creating } = this.state

    var columns = [
      {
        title: 'Code',
        dataIndex: 'insurance_provider_code',
        key: 'code',
        width: 120
      },
      {
        title: 'Name',
        dataIndex: 'insurance_provider_name',
        key: 'name',
        render: (text, record) => (
          <a onClick={() => this.openDetail(record.id)}>{text}</a>
        )
      },
      {
        title: 'Phone',
        dataIndex: 'phone_number',
        key: 'phone',
        width: 160
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (s) => <Tag color={statusColor(s)}>{s.replace('_', ' ').toUpperCase()}</Tag>
      },
      {
        title: 'Active',
        dataIndex: 'is_active',
        key: 'active',
        width: 80,
        render: (v) => v ? 'Yes' : 'No'
      },
      {
        title: 'Recordings',
        dataIndex: 'recording_count',
        key: 'recordings',
        width: 100
      },
      {
        title: '',
        key: 'actions',
        width: 100,
        render: (_, record) => (
          <Button type='link' icon={<EditOutlined />} onClick={() => this.openDetail(record.id)}>
            Edit
          </Button>
        )
      }
    ]

    var usedProviderIds = configs.map(c => c.insurance_provider_id)
    var availableProviders = providers.filter(p => !usedProviderIds.includes(p.id))

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 0' }}>
          <Title level={3} style={{ margin: 0 }}>
            Insurance Call Config
          </Title>
          <Space>
            <Button icon={<UploadOutlined />} onClick={this.openIntakeModal}>
              Upload recording
            </Button>
            <Button type='primary' icon={<PlusOutlined />} className='btn-provider' onClick={this.openCreateModal}>
              Add carrier
            </Button>
          </Space>
        </div>
        <p style={{ margin: '8px 0 0', color: '#666', maxWidth: 720 }}>
          Configure AI voice agent call settings for each insurance carrier. Upload a sample
          recording and we&rsquo;ll auto-detect the carrier and extract the IVR, verification,
          and conversation flow for you.
        </p>
        <Spin spinning={loading}>
          <div className='office-admin-card'>
            <Table
              rowKey='id'
              columns={columns}
              dataSource={configs}
              pagination={false}
            />
          </div>
        </Spin>

        <Modal
          title='Add Carrier Call Config'
          open={createModalOpen}
          onCancel={this.closeCreateModal}
          footer={null}
          destroyOnClose
        >
          <Form layout='vertical' onFinish={this.handleCreate}>
            <Form.Item name='insurance_provider_id' label='Insurance Carrier' rules={[{ required: true }]}>
              <Select placeholder='Select carrier'>
                {availableProviders.map(p => (
                  <Select.Option key={p.id} value={p.id}>{p.code} &mdash; {p.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name='phone_number' label='Provider Services Phone Number'>
              <Input placeholder='+1XXXXXXXXXX' />
            </Form.Item>
            <Form.Item>
              <Button type='primary' htmlType='submit' loading={creating}>Create</Button>
            </Form.Item>
          </Form>
        </Modal>

        {this.renderIntakeModal()}
      </>
    )
  }

  // ── Render: Intake Modal ──

  renderIntakeModal () {
    var { intakeModalOpen, intakeFile, intakeUploading, intakeRecording, intakeBinding,
          intakeOverrideProviderId, providers } = this.state

    var rec = intakeRecording
    var stage = !rec ? 'pre' : rec.status

    var stageMessage = ({
      uploaded: 'Queued for transcription...',
      transcribing: 'Transcribing audio with Whisper. This may take a minute or two...',
      identified: '',
      failed: rec && rec.error_message ? rec.error_message : 'Something went wrong.'
    })[stage] || ''

    return (
      <Modal
        title='Upload Recording'
        open={intakeModalOpen}
        onCancel={this.closeIntakeModal}
        footer={null}
        destroyOnClose
        width={640}
        maskClosable={!intakeUploading && !intakeBinding}
      >
        {stage === 'pre' && (
          <>
            <p style={{ color: '#666', marginTop: 0 }}>
              Drop a sample call recording. We&rsquo;ll transcribe it, detect which insurance
              carrier it belongs to, and pre-fill the configuration for your review.
            </p>
            <Upload.Dragger
              accept='audio/*'
              multiple={false}
              maxCount={1}
              beforeUpload={(file) => { this.setState({ intakeFile: file }); return false }}
              onRemove={() => this.setState({ intakeFile: null })}
              fileList={intakeFile ? [intakeFile] : []}
            >
              <p className='ant-upload-drag-icon'><UploadOutlined /></p>
              <p className='ant-upload-text'>Click or drag an audio file to upload</p>
              <p className='ant-upload-hint' style={{ color: '#888' }}>
                .mp3, .wav, .m4a — typical files are 5-30 minutes long.
              </p>
            </Upload.Dragger>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Button onClick={this.closeIntakeModal}>Cancel</Button>
              <Button
                type='primary'
                disabled={!intakeFile}
                loading={intakeUploading}
                onClick={this.handleIntakeUpload}
              >
                Upload &amp; identify
              </Button>
            </div>
          </>
        )}

        {(stage === 'uploaded' || stage === 'transcribing') && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Spin size='large' />
            <p style={{ marginTop: 16, color: '#666' }}>{stageMessage}</p>
            <Tag color={recordingStatusColor(stage)}>{stage.toUpperCase()}</Tag>
          </div>
        )}

        {stage === 'failed' && (
          <div style={{ padding: '16px 0' }}>
            <Card size='small' style={{ background: '#fff2f0', borderColor: '#ffccc7' }}>
              <Text type='danger' strong>Recording could not be processed.</Text>
              <br />
              <Text>{stageMessage}</Text>
            </Card>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Button onClick={this.closeIntakeModal}>Close</Button>
            </div>
          </div>
        )}

        {stage === 'identified' && this.renderIntakeSuggestion(rec, intakeOverrideProviderId, intakeBinding, providers)}
      </Modal>
    )
  }

  renderIntakeSuggestion (rec, overrideProviderId, binding, providers) {
    var suggested = rec.suggested_provider
    var alternates = rec.alternates || []
    var chosenId = overrideProviderId || (suggested && suggested.provider_id) || null
    var hasSuggestion = !!suggested

    return (
      <>
        {hasSuggestion ? (
          <Card
            size='small'
            style={{ marginBottom: 12, borderColor: '#b7eb8f', background: '#f6ffed' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text style={{ color: '#666' }}>We think this is</Text>
                <Title level={4} style={{ margin: '4px 0' }}>
                  {suggested.code} &mdash; {suggested.name}
                </Title>
              </div>
              <Tag color='green'>{Math.round((suggested.confidence || 0) * 100)}% confidence</Tag>
            </div>
          </Card>
        ) : (
          <Card size='small' style={{ marginBottom: 12, background: '#fffbe6', borderColor: '#ffe58f' }}>
            <Text strong>We couldn&rsquo;t auto-detect a carrier from this recording.</Text>
            <br />
            <Text type='secondary'>Review the transcript below and pick the matching carrier.</Text>
          </Card>
        )}

        {alternates.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text style={{ color: '#888', fontSize: 12 }}>OR ONE OF</Text>
            <div style={{ marginTop: 4 }}>
              {alternates.map(a => (
                <Button
                  key={a.provider_id}
                  size='small'
                  type={chosenId === a.provider_id ? 'primary' : 'default'}
                  style={{ marginRight: 8, marginBottom: 4 }}
                  onClick={() => this.setState({ intakeOverrideProviderId: a.provider_id })}
                >
                  {a.code} ({Math.round(a.confidence * 100)}%)
                </Button>
              ))}
              {suggested && (
                <Button
                  size='small'
                  type={chosenId === suggested.provider_id ? 'primary' : 'default'}
                  style={{ marginRight: 8, marginBottom: 4 }}
                  onClick={() => this.setState({ intakeOverrideProviderId: null })}
                >
                  {suggested.code} (suggested)
                </Button>
              )}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <Text style={{ color: '#888', fontSize: 12 }}>
            {hasSuggestion ? 'OR PICK FROM FULL LIST' : 'PICK A CARRIER'}
          </Text>
          <Select
            showSearch
            allowClear
            placeholder='Select an insurance carrier'
            optionFilterProp='children'
            style={{ width: '100%', marginTop: 4 }}
            value={chosenId || undefined}
            onChange={(v) => this.setState({ intakeOverrideProviderId: v || null })}
          >
            {(providers || []).map(p => (
              <Select.Option key={p.id} value={p.id}>{p.code} &mdash; {p.name}</Select.Option>
            ))}
          </Select>
        </div>

        {rec.transcript && (
          <details style={{ marginBottom: 12 }} open={!hasSuggestion}>
            <summary style={{ cursor: 'pointer', color: '#666' }}>
              {hasSuggestion ? 'Show transcript' : 'Transcript'} ({rec.transcript.length} chars)
            </summary>
            <Paragraph style={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto', background: '#fafafa', padding: 12, borderRadius: 4, marginTop: 8 }}>
              {rec.transcript}
            </Paragraph>
          </details>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={this.closeIntakeModal} disabled={binding}>Cancel</Button>
          <Button
            type='primary'
            loading={binding}
            disabled={!chosenId}
            onClick={this.handleIntakeConfirm}
          >
            Use this carrier
          </Button>
        </div>
      </>
    )
  }

  // ── Render: Detail View ──

  renderDetail () {
    var { detail, detailLoading, saving, editedFields, testCallModalOpen, testCallPolicyId, testCalling } = this.state

    if (detailLoading || !detail) {
      return <Spin size='large' style={{ display: 'block', margin: '80px auto' }} />
    }

    var getVal = (field) => field in editedFields ? editedFields[field] : detail[field]

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={this.backToList}>Back</Button>
            <Title level={3} style={{ margin: 0 }}>
              {detail.insurance_provider_code} &mdash; {detail.insurance_provider_name}
            </Title>
            <Tag color={statusColor(detail.status)}>{detail.status.replace('_', ' ').toUpperCase()}</Tag>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {detail.status === 'pending_review' && (
              <Button type='primary' icon={<CheckCircleOutlined />} onClick={this.handleApprove}>
                Approve &amp; Activate
              </Button>
            )}
            {detail.is_active && (
              <Button danger icon={<CloseCircleOutlined />} onClick={this.handleDisable}>
                Disable
              </Button>
            )}
            {!detail.is_active && detail.status !== 'pending_review' && detail.status !== 'draft' && (
              <Button type='primary' icon={<CheckCircleOutlined />} onClick={this.handleApprove}>
                Re-activate
              </Button>
            )}
            <Button icon={<PhoneOutlined />} onClick={this.openTestCallModal}>Test Call</Button>
            <Button icon={<ReloadOutlined />} onClick={() => this.openDetail(detail.id)}>Refresh</Button>
          </div>
        </div>

        {detail.status === 'pending_review' && (
          <Card size='small' style={{ margin: '16px 0 0', background: '#fffbe6', borderColor: '#ffe58f' }}>
            <Text strong>This carrier config has been auto-generated from recording analysis and is awaiting your review.</Text>
            <br />
            <Text>Review the configuration, edit the prompt if needed, then click "Approve &amp; Activate" to enable calls.</Text>
          </Card>
        )}

        <div className='office-admin-card'>
          <Tabs defaultActiveKey='recordings'>
            <TabPane tab='Recordings' key='recordings'>
              {this.renderRecordingsTab()}
            </TabPane>
            <TabPane tab='Configuration' key='config'>
              {this.renderConfigTab(getVal)}
            </TabPane>
            <TabPane tab='Prompt' key='prompt'>
              {this.renderPromptTab(getVal)}
            </TabPane>
          </Tabs>
        </div>

        {Object.keys(editedFields).length > 0 && (
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button type='primary' loading={saving} onClick={this.handleSave} size='large'>
              Save Changes
            </Button>
          </div>
        )}

        <Modal
          title='Initiate Test Call'
          open={testCallModalOpen}
          onCancel={this.closeTestCallModal}
          onOk={this.handleTestCall}
          confirmLoading={testCalling}
          okText='Start Call'
        >
          <p>Enter a test patient's insurance policy ID to call this carrier:</p>
          <Input
            placeholder='Policy ID'
            value={testCallPolicyId}
            onChange={e => this.setState({ testCallPolicyId: e.target.value })}
          />
        </Modal>
      </>
    )
  }

  renderRecordingsTab () {
    var { detail } = this.state
    var recordings = detail.recordings || []

    var columns = [
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (s) => <Tag color={recordingStatusColor(s)}>{s.toUpperCase()}</Tag>
      },
      {
        title: 'Uploaded',
        dataIndex: 'created_at',
        key: 'date',
        width: 180,
        render: (d) => new Date(d).toLocaleString()
      },
      {
        title: 'Transcript',
        dataIndex: 'transcript',
        key: 'transcript',
        render: (t) => t
          ? <Paragraph ellipsis={{ rows: 2, expandable: true }}>{t}</Paragraph>
          : <Text type='secondary'>Pending...</Text>
      },
      {
        title: 'Analysis',
        dataIndex: 'analysis',
        key: 'analysis',
        width: 120,
        render: (a) => a
          ? <Tag color='success'>Complete</Tag>
          : <Tag color='default'>Pending</Tag>
      }
    ]

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={5} style={{ margin: 0 }}>Sample Call Recordings</Title>
          <Upload
            beforeUpload={this.handleUpload}
            showUploadList={false}
            accept='audio/*'
          >
            <Button icon={<UploadOutlined />} type='primary'>Upload Recording</Button>
          </Upload>
        </div>

        <p style={{ margin: '0 0 16px', color: '#666' }}>
          Upload a sample recording of a real call to this insurance carrier. The system will
          automatically transcribe it and extract IVR steps, verification questions, and
          conversation patterns.
        </p>

        <Table
          columns={columns}
          dataSource={recordings}
          rowKey='id'
          pagination={false}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: 16 }}>
                {record.transcript && (
                  <>
                    <Title level={5}>Full Transcript</Title>
                    <Paragraph style={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto', background: '#fafafa', padding: 12, borderRadius: 4 }}>
                      {record.transcript}
                    </Paragraph>
                  </>
                )}
                {record.analysis && (
                  <>
                    <Divider />
                    <Title level={5}>Analysis Results</Title>
                    <Descriptions bordered size='small' column={1}>
                      {record.analysis.ivr_sequence && (
                        <Descriptions.Item label='IVR Sequence'>
                          {record.analysis.ivr_sequence.map((step, i) => (
                            <div key={i}>Step {step.step}: {step.description} &rarr; press <strong>{step.digits}</strong> (wait {step.wait_seconds}s)</div>
                          ))}
                        </Descriptions.Item>
                      )}
                      {record.analysis.verification_fields && (
                        <Descriptions.Item label='Verification Fields'>
                          {record.analysis.verification_fields.join(', ')}
                        </Descriptions.Item>
                      )}
                      {record.analysis.conversation_guide && (
                        <Descriptions.Item label='Conversation Guide'>
                          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{record.analysis.conversation_guide}</Paragraph>
                        </Descriptions.Item>
                      )}
                      {record.analysis.carrier_quirks && (
                        <Descriptions.Item label='Carrier Quirks'>
                          {record.analysis.carrier_quirks}
                        </Descriptions.Item>
                      )}
                      {record.analysis.estimated_hold_time_minutes != null && (
                        <Descriptions.Item label='Est. Hold Time'>
                          {record.analysis.estimated_hold_time_minutes} minutes
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </>
                )}
                {record.error_message && (
                  <Text type='danger'>Error: {record.error_message}</Text>
                )}
              </div>
            )
          }}
        />
      </>
    )
  }

  renderConfigTab (getVal) {
    return (
      <div style={{ maxWidth: 800 }}>
        <Descriptions bordered column={1} size='small' style={{ marginBottom: 24 }}>
          <Descriptions.Item label='Carrier'>{this.state.detail.insurance_provider_code} &mdash; {this.state.detail.insurance_provider_name}</Descriptions.Item>
          <Descriptions.Item label='Status'><Tag color={statusColor(this.state.detail.status)}>{this.state.detail.status}</Tag></Descriptions.Item>
        </Descriptions>

        <Form layout='vertical'>
          <Form.Item label='Phone Number'>
            <Input
              value={getVal('phone_number')}
              onChange={e => this.handleFieldChange('phone_number', e.target.value)}
              placeholder='+1XXXXXXXXXX'
            />
          </Form.Item>

          {/* READ-ONLY on purpose. The agent takes its carrier knowledge from
              the carrier playbook now (services/carrier_playbooks.knowledge_for_voice),
              falling back to these stored values only when no playbook exists. Left
              editable, this form would accept a change, save it, and the agent
              would keep using the playbook — a ghost to debug. Edit it where it
              actually lives. */}
          <Alert
            type='info'
            showIcon
            style={{ marginBottom: 12 }}
            message='Carrier knowledge is managed in Carrier Knowledge'
            description={
              <span>
                The voice agent reads the IVR path, verification fields and
                conversation notes from this carrier&rsquo;s playbook — the same text
                FTC staff see and correct. The values below are what it will use.{' '}
                <a href='#/carrier-knowledge'>Open Carrier Knowledge →</a>
              </span>
            }
          />

          <Form.Item label='IVR Sequence (JSON)'>
            <TextArea
              readOnly
              rows={6}
              value={typeof getVal('ivr_sequence') === 'string' ? getVal('ivr_sequence') : JSON.stringify(getVal('ivr_sequence'), null, 2)}
              style={{ fontFamily: 'monospace', fontSize: 12, background: '#faf9fd' }}
            />
          </Form.Item>

          <Form.Item label='Verification Fields'>
            <Select
              mode='tags'
              open={false}
              value={getVal('verification_fields') || []}
              style={{ width: '100%', pointerEvents: 'none', background: '#faf9fd' }}
            />
          </Form.Item>

          <Form.Item label='Conversation Guide'>
            <TextArea
              readOnly
              rows={6}
              value={getVal('conversation_guide')}
              style={{ background: '#faf9fd' }}
              placeholder='Set in Carrier Knowledge.'
            />
          </Form.Item>

          <Form.Item label='Max Call Duration (seconds)'>
            <Input
              type='number'
              value={getVal('max_call_duration')}
              onChange={e => this.handleFieldChange('max_call_duration', parseInt(e.target.value) || 1800)}
              style={{ width: 200 }}
            />
          </Form.Item>
        </Form>
      </div>
    )
  }

  renderPromptTab (getVal) {
    var generated = this.state.detail.generated_prompt || ''
    var current = getVal('prompt_override') || ''

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={5} style={{ margin: 0 }}>Custom Prompt Override <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span></Title>
          {generated && (
            <Button
              size='small'
              onClick={() => this.handleFieldChange('prompt_override', generated)}
            >
              Reset to Generated
            </Button>
          )}
        </div>

        <p style={{ margin: '0 0 12px', color: '#666' }}>
          Leave this blank for normal use. The agent has its own built-in
          conversation prompt and automatically layers on this carrier's
          Configuration (IVR menu path, verification fields, and notes from the
          recording). Only fill this in to <b>fully replace</b> the agent's
          conversation prompt for a carrier that needs special handling.
          Changes take effect after saving.
        </p>

        <TextArea
          rows={24}
          value={current}
          onChange={e => this.handleFieldChange('prompt_override', e.target.value)}
          style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: '1.5' }}
        />
      </div>
    )
  }

  render () {
    var { profileSummary } = this.props

    return (
      <DashboardLayout
        logo={profileSummary && profileSummary.logo ? profileSummary.logo : ''}
        footerLogo={this.state.serverLogo}
        breadCrumb='Insurance Call Config'
      >
        <div className='paymentRequestContent'>
          {this.state.selectedId ? this.renderDetail() : this.renderList()}
        </div>
      </DashboardLayout>
    )
  }
}

function mapStateToProps (state) {
  return {}
}

export default connect(mapStateToProps)(InsuranceCarrierCallAdmin)
